<template>
    <div id="man-hour-dashboard">
        <div class="header-block d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
                <div class="page-title">Dashboard Hora Homem</div>
                <div class="date-range-info" v-if="dateRangeText">{{ dateRangeText }}</div>
            </div>

            <b-button class="btn-filters" variant="outline-brown" size="sm" @click="filtersOpen = true">
                <feather-icon icon="FilterIcon" class="mr-50" />
                Filtros
            </b-button>
        </div>

        <div class="body-block">
            <!-- KPIs -->
            <div class="kpi-grid mb-2">
                <b-card class="kpi-card text-center">
                    <div class="kpi-title">Total de Horas</div>
                    <div class="kpi-value-large">{{ totalManHours }}</div>
                </b-card>
                <b-card class="kpi-card text-center">
                    <div class="kpi-title">Total de Apontamentos</div>
                    <div class="kpi-value-large">{{ filteredAppointments.length }}</div>
                </b-card>
                <b-card class="kpi-card text-center">
                    <div class="kpi-title">Operadores Únicos</div>
                    <div class="kpi-value-large">{{ uniqueOperatorsCount }}</div>
                </b-card>
            </div>

            <b-row class="chart-row">
                <b-col cols="12" class="mb-1">
                    <b-card class="chart-card h-100">
                        <div class="chart-header">Horas por Turno</div>
                        <div v-if="loading" class="text-center p-5">
                            <b-spinner variant="primary" label="Carregando..."></b-spinner>
                        </div>
                        <app-echart-bar v-else :optionData="barOption" :height="barHeight" />
                    </b-card>
                </b-col>
            </b-row>

            <!-- Table Section -->
            <b-row class="chart-row">
                <b-col cols="12" class="mb-1">
                    <b-card class="chart-card h-100">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <div class="chart-header mb-0">Detalhamento de Apontamentos</div>
                            <b-button variant="brown" size="sm" @click="exportToCSV">
                                <feather-icon icon="DownloadIcon" class="mr-50" />
                                Exportar CSV
                            </b-button>
                        </div>

                        <b-table responsive :items="tableItems" :fields="tableFields" :busy="loading" show-empty
                            empty-text="Nenhum apontamento encontrado" striped hover small head-variant="light"
                            :per-page="perPage" :current-page="currentPage">
                            <template #cell(operators)="data">
                                <div v-for="(op, index) in data.value" :key="index" class="small">
                                    {{ op }}
                                </div>
                            </template>
                            <template #table-busy>
                                <div class="text-center my-2">
                                    <b-spinner class="align-middle"></b-spinner>
                                    <strong>Carregando...</strong>
                                </div>
                            </template>
                        </b-table>

                        <div class="d-flex justify-content-end mt-1">
                            <b-pagination v-model="currentPage" :total-rows="tableItems.length" :per-page="perPage"
                                align="right" size="sm" class="my-0" />
                        </div>
                    </b-card>
                </b-col>
            </b-row>
        </div>

        <!-- Sidebar: Filters -->
        <b-sidebar right shadow bg-variant="white" width="320px" v-model="filtersOpen" backdrop>
            <template #header>
                <div class="d-flex justify-content-between align-items-center w-100 px-1 py-1">
                    <span class="font-weight-bold">Filtros</span>
                    <span style="cursor: pointer" @click="filtersOpen = false">
                        <feather-icon icon="XIcon" size="18" />
                    </span>
                </div>
            </template>

            <div class="filters-card">
                <div class="filter">
                    <label class="filter-label">De:</label>
                    <input type="date" v-model="filters.startDate" class="date-input-sidebar" />
                </div>
                <div class="filter">
                    <label class="filter-label">Até:</label>
                    <input type="date" v-model="filters.endDate" class="date-input-sidebar" />
                </div>
                <div class="filter">
                    <label class="filter-label">Operadores:</label>
                    <v-select v-model="filters.selectedUsers" :options="userOptions" label="name" multiple
                        placeholder="Todos os operadores" :reduce="option => option.id" />
                </div>
                <div class="filter">
                    <label class="filter-label">Turnos:</label>
                    <v-select v-model="filters.selectedShifts" :options="shiftOptions" label="name" multiple
                        placeholder="Todos os turnos" :reduce="option => option.id" />
                </div>
            </div>

            <template #footer>
                <div class="d-flex justify-content-end px-1 pb-1">
                    <b-button size="sm" class="mr-50" variant="outline-secondary"
                        @click="resetFilters">Limpar</b-button>
                    <b-button size="sm" variant="brown" @click="applyFilters">Aplicar</b-button>
                </div>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import vSelect from 'vue-select'
import moment from 'moment'
import { BCard, BRow, BCol, BButton, BTable, BSpinner, BSidebar, BFormGroup, BPagination } from 'bootstrap-vue'
import jwtDefaultConfig from "@core/auth/jwt/jwtDefaultConfig"
import AppEchartBar from "@core/components/charts/echart/AppEchartBar.vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";

export default {
    name: 'ManHourDashboard',
    components: {
        vSelect,
        BCard, BRow, BCol, BButton, BTable, BSpinner, BSidebar, BFormGroup, BPagination,
        AppEchartBar,
        FeatherIcon
    },
    data() {
        return {
            loading: false,
            filtersOpen: false,
            currentSite: null,
            windowWidth: typeof window !== "undefined" ? window.innerWidth : 1440,

            // Data Sources
            appointments: [],
            shifts: [],
            users: [],

            // Filters
            filters: {
                startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
                endDate: moment().format('YYYY-MM-DD'),
                selectedUsers: [],
                selectedShifts: []
            },
            filterDays: 7,
            quickFilterDays: [7, 30, 60, 90],

            perPage: 10,
            currentPage: 1,

            tableFields: [
                { key: 'id', label: 'ID', sortable: true },
                { key: 'startTime', label: 'Início', sortable: true },
                { key: 'endTime', label: 'Fim', sortable: true },
                { key: 'duration', label: 'Duração (h)', sortable: true },
                { key: 'shiftName', label: 'Turno', sortable: true },
                { key: 'operators', label: 'Operadores' },
                { key: 'operationId', label: 'Operação', sortable: true }
            ],

            barOption: {
                legend: {
                    left: 10,
                    top: 6,
                    orient: "horizontal",
                    itemWidth: 12,
                    itemHeight: 8,
                    itemGap: 8,
                    icon: "roundRect",
                    textStyle: { fontSize: 12 }
                },
                grid: {
                    left: "40px",
                    right: "10px",
                    bottom: "20px",
                    top: 50,
                    containLabel: true
                },
                xAxis: {
                    type: "category",
                    axisLabel: { rotate: 45, fontSize: 10 },
                    data: []
                },
                yAxis: { type: "value", splitLine: { show: false } },
                series: [
                    {
                        name: "Horas Homem",
                        type: "bar",
                        data: [],
                        barMaxWidth: 40,
                        itemStyle: { color: "#f3811a" }
                    }
                ]
            },
        }
    },
    computed: {
        barHeight() {
            const w = this.windowWidth;
            if (w >= 1400) return 420;
            if (w >= 1200) return 380;
            if (w >= 992) return 340;
            if (w >= 768) return 300;
            return 260;
        },
        userOptions() {
            const unique = new Map();
            this.users.forEach(u => {
                if (!unique.has(u.guid)) {
                    unique.set(u.guid, {
                        id: u.guid,
                        name: `${u.firstName} ${u.lastName}`.trim()
                    });
                }
            });
            return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name));
        },
        shiftOptions() {
            return this.shifts.map(s => ({
                id: s.id,
                name: s.name || s.description || `Turno ${s.id}`
            }));
        },
        dateRangeText() {
            if (this.filters.startDate && this.filters.endDate) {
                const start = moment(this.filters.startDate).format('DD/MM/YYYY');
                const end = moment(this.filters.endDate).format('DD/MM/YYYY');
                return `${start} - ${end}`;
            }
            return '';
        },
        filteredAppointments() {
            let items = [...this.appointments];

            // Filter by Date
            if (this.filters.startDate && this.filters.endDate) {
                const start = moment(this.filters.startDate).startOf('day');
                const end = moment(this.filters.endDate).endOf('day');

                items = items.filter(item => {
                    const itemDate = moment(item.startTime);
                    return itemDate.isBetween(start, end, null, '[]');
                });
            }

            // Filter by Users
            if (this.filters.selectedUsers && this.filters.selectedUsers.length > 0) {
                items = items.filter(item => {
                    return item.operators.some(op => this.filters.selectedUsers.includes(op.userGuid));
                });
            }

            // Filter by Shifts
            if (this.filters.selectedShifts && this.filters.selectedShifts.length > 0) {
                items = items.filter(item => this.filters.selectedShifts.includes(item.shiftId));
            }

            return items;
        },
        tableItems() {
            return this.filteredAppointments.map(item => {
                const shift = this.shifts.find(s => s.id === item.shiftId);
                const shiftName = shift ? (shift.name || shift.description) : `ID ${item.shiftId}`;

                const durationMs = moment(item.endTime).diff(moment(item.startTime));
                const durationHours = (durationMs / (1000 * 60 * 60)).toFixed(2);

                const operatorNames = item.operators.map(op => {
                    const user = this.users.find(u => u.guid === op.userGuid);
                    return user ? `${user.firstName} ${user.lastName}`.trim() : op.userGuid;
                });

                return {
                    id: item.id,
                    startTime: moment(item.startTime).format('DD/MM/YYYY HH:mm'),
                    endTime: moment(item.endTime).format('DD/MM/YYYY HH:mm'),
                    duration: durationHours,
                    shiftName: shiftName,
                    shiftId: item.shiftId,
                    operators: operatorNames,
                    rawOperators: item.operators,
                    operationId: item.operationId,
                    rawDurationMs: durationMs
                };
            });
        },
        totalManHours() {
            let totalMs = 0;
            this.tableItems.forEach(item => {
                totalMs += item.rawDurationMs * item.rawOperators.length;
            });
            return (totalMs / (1000 * 60 * 60)).toFixed(2) + ' h';
        },
        uniqueOperatorsCount() {
            const uniqueOps = new Set();
            this.filteredAppointments.forEach(item => {
                item.operators.forEach(op => uniqueOps.add(op.userGuid));
            });
            return uniqueOps.size;
        }
    },
    watch: {
        tableItems: {
            handler(items) {
                this.updateBarChart(items);
            },
            deep: true
        }
    },
    mounted() {
        if (typeof window !== "undefined") {
            this._resizeHandler = () => (this.windowWidth = window.innerWidth);
            window.addEventListener("resize", this._resizeHandler);
        }
    },
    beforeDestroy() {
        if (this._resizeHandler) window.removeEventListener("resize", this._resizeHandler);
    },
    async created() {
        const userData = JSON.parse(localStorage.getItem('userData'));
        this.currentSite = userData ? userData.siteId : 212;

        await this.fetchInitialData();
        this.fetchData();
    },
    methods: {
        async fetchInitialData() {
            this.loading = true;
            try {
                const token = localStorage.getItem(jwtDefaultConfig.storageTokenKeyName);
                const config = {
                    headers: { Authorization: `${jwtDefaultConfig.tokenType} ${token}` }
                };

                const shiftsRes = await this.$http2.get(`/api/Shift?siteId=${this.currentSite}`, config);
                this.shifts = shiftsRes.data.data || shiftsRes.data || [];

                const usersRes = await this.$http2.get('/api/myconfig/users', config);
                this.users = usersRes.data.data || usersRes.data || [];

            } catch (error) {
                console.error("Error fetching initial data:", error);
                this.$bvToast.toast('Erro ao carregar dados iniciais', {
                    title: 'Erro',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loading = false;
            }
        },
        async fetchData() {
            this.loading = true;
            try {
                const token = localStorage.getItem(jwtDefaultConfig.storageTokenKeyName);
                const config = {
                    headers: { Authorization: `${jwtDefaultConfig.tokenType} ${token}` }
                };

                const response = await this.$http2.get(`/api/man-hour-appointment?siteId=${this.currentSite}`, config);
                this.appointments = response.data.data || response.data || [];

            } catch (error) {
                console.error("Error fetching appointments:", error);
                this.$bvToast.toast('Erro ao carregar apontamentos', {
                    title: 'Erro',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loading = false;
                this.filtersOpen = false;
            }
        },
        updateBarChart(items) {
            const dataMap = {};
            items.forEach(item => {
                const shiftName = item.shiftName;
                if (!dataMap[shiftName]) dataMap[shiftName] = 0;
                const hours = (item.rawDurationMs * item.rawOperators.length) / (1000 * 60 * 60);
                dataMap[shiftName] += hours;
            });

            const categories = Object.keys(dataMap);
            const data = categories.map(cat => parseFloat(dataMap[cat].toFixed(2)));

            this.barOption.xAxis.data = categories;
            this.barOption.series[0].data = data;
        },
        applyFilters() {
            this.filtersOpen = false;
        },
        resetFilters() {
            this.filters = {
                startDate: moment().startOf('month').format('YYYY-MM-DD'),
                endDate: moment().endOf('month').format('YYYY-MM-DD'),
                selectedUsers: [],
                selectedShifts: []
            };
            this.fetchData();
        },
        exportToCSV() {
            const items = this.tableItems;
            if (items.length === 0) return;

            const headers = ['ID', 'Início', 'Fim', 'Duração (h)', 'Turno', 'Operadores', 'Operação'];
            const csvContent = [
                headers.join(','),
                ...items.map(item => [
                    item.id,
                    item.startTime,
                    item.endTime,
                    item.duration,
                    `"${item.shiftName}"`,
                    `"${item.operators.join(', ')}"`,
                    item.operationId
                ].join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `man_hour_export_${moment().format('YYYYMMDD_HHmm')}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#man-hour-dashboard {
    .header-block {
        padding: 10px 14px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .page-title {
        font-size: 18px;
        font-weight: 700;
        color: #4c4541;
    }

    .date-range-info {
        font-size: 12px;
        color: #6c757d;
        margin-left: 12px;
        font-weight: 500;
    }

    .body-block {
        padding: 4px 14px 20px 14px;
    }

    .kpi-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1.5rem;
        margin-bottom: 1.5rem;
    }

    .kpi-card {
        height: 100%;
        border: none;
        box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
        margin-bottom: 0;
    }

    .kpi-title {
        font-size: 14px;
        font-weight: 600;
        color: #6e6b7b;
        margin-bottom: 0.5rem;
    }

    .kpi-value-large {
        font-size: 24px;
        font-weight: 700;
        color: #5e5873;
    }

    .chart-header {
        font-size: 16px;
        font-weight: 600;
        color: #5e5873;
        margin-bottom: 1rem;
    }

    .filters-card {
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 6px;
        padding: 12px;

        .filter+.filter {
            margin-top: 8px;
        }

        .filter-label {
            display: block;
            font-weight: 600;
            font-size: 12px;
            margin-bottom: 6px;
        }

        .date-input-sidebar {
            width: 100%;
            padding: 6px 8px;
            border: 1px solid #d8d6de;
            border-radius: 4px;
            font-size: 14px;
            color: #6c757d;
            background-color: #fff;
            cursor: pointer;
            transition: border-color 0.2s;

            &:hover {
                border-color: #974900;
            }

            &:focus {
                outline: none;
                border-color: #974900;
                box-shadow: 0 0 0 2px rgba(151, 73, 0, 0.1);
            }
        }

        .quick-filter-buttons {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
        }

        .quick-filter-btn {
            padding: 6px 10px;
            border: 1px solid #d8d6de;
            border-radius: 4px;
            background-color: #fff;
            color: #6c757d;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
                border-color: #974900;
                color: #974900;
            }

            &.active {
                background-color: #974900;
                border-color: #974900;
                color: #fff;
            }
        }
    }

    // Custom Button Variants for Brown Theme
    .btn-brown {
        background-color: #974900 !important;
        border-color: #974900 !important;
        color: #fff !important;

        &:hover,
        &:focus,
        &:active,
        &.active {
            background-color: #8a4300 !important;
            border-color: #8a4300 !important;
            color: #fff !important;
            box-shadow: none !important;
        }
    }

    .btn-outline-brown {
        background-color: transparent !important;
        border-color: #974900 !important;
        color: #974900 !important;

        &:hover,
        &:focus,
        &:active,
        &.active {
            background-color: rgba(151, 73, 0, 0.1) !important;
            border-color: #974900 !important;
            color: #974900 !important;
            box-shadow: none !important;
        }
    }
}
</style>
